<script lang="ts" setup>
import LineX from '@docue/docue-ui-v2/icons/LineX.vue'

import type { TimerController, Toast } from '~/types/ui'

const props = withDefaults(defineProps<Toast>(), {
  timeout: 5000,
})

const { remove } = useToast()
let timer: TimerController | undefined
const remaining = ref(props.timeout)

const close = () => {
  remove(props.id)
  timer?.stop()
}

const pause = () => {
  timer?.pause()
}

const resume = () => {
  timer?.resume()
}

onMounted(() => {
  timer = useTimer(() => {
    close()
  }, props.timeout)

  watchEffect(() => {
    remaining.value = timer?.remaining.value || 0
  })
})

onUnmounted(() => {
  if (timer)
    timer.stop()
})

const progressStyle = computed(() => {
  const remainingPrecent = remaining.value / props.timeout * 100

  return { width: `${remainingPrecent || 0}%` }
})
</script>

<template>
  <Transition
    appear
    enter-active-class="transform ease-out duration-300 transition"
    enter-from-class="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
    enter-to-class="translate-y-0 opacity-100 sm:translate-x-0"
    leave-active-class="transition ease-in duration-100"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <DTAlert
      :variant="variant"
      class="w-80 flex-col drop-shadow"
      @mouseenter="pause"
      @mouseleave="resume"
    >
      <div class="flex w-full grow items-start justify-between gap-2">
        <div
          class="flex min-w-40 grow items-start justify-start gap-2 pt-0.5"
        >
          <span class="shrink-0 p-0.5 empty:hidden">
            <Component
              :is="icon"
              v-if="icon"
              class="size-5"
            />
          </span>
          <span class="whitespace-pre-line">{{ content }}</span>
        </div>
        <DTIconButton
          variant="plain"
          class="ml-5 shrink-0 text-inherit"
          size="sm"
          @click="close"
        >
          <LineX class="size-4 text-inherit" />
        </DTIconButton>
      </div>
      <div
        class="ml-0 mr-auto h-1 rounded-xl"
        :class="{
          'bg-blue-600/20': variant === 'blue',
          'bg-red-600/20': variant === 'red',
          'bg-green-600/20': variant === 'green',
          'bg-yellow-600/20': variant === 'yellow',
          'bg-gray-600/20': variant === 'default' || variant === undefined,
        }"
        :style="progressStyle"
      />
    </DTAlert>
  </Transition>
</template>
